import Pill from '@pelckmans/elementis/components/pill';
import { ComponentProps, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Module } from '../api/module';
import IdentityContext from '../contexts/IdentityContext';
import { isLicensed, MODULE_LICENSE } from '../core/access/module-access';
import useModuleAccess from '../hooks/useModuleAccess';
import MethodModuleStatus from './tooltip/MethodModuleStatus';

const BASE = 'MODULES.STATUS';

function ModuleStatus({
  module,
  showUnlicensedStatus = false,
  persistTooltip = false,
}: {
  module: Module;
  showUnlicensedStatus?: boolean;
  persistTooltip?: boolean;
}) {
  const { identity } = useContext(IdentityContext);
  const { t } = useTranslation();
  const { id, publishingState, incomplete } = module;
  const { canOpen, license } = useModuleAccess(module);
  const isDemo = license === MODULE_LICENSE.DEMO;

  const isAuthor = identity.author_modules.includes(module.id);

  const isUnlicensed =
    showUnlicensedStatus &&
    (module.publishingState === 'published' || module.publishingState === 'phasedOut') &&
    !isLicensed(license);

  const role = t(`ROLES.${isAuthor ? 'AUTHOR' : identity.highestRole.toUpperCase()}`);

  const renderStatus = ({
    key,
    label,
    tooltip,
    prominence,
  }: {
    key: string;
    label: string;
    tooltip?: string;
    prominence: ComponentProps<typeof Pill>['prominence'];
  }) => {
    const pill = (
      <Pill prominence={prominence} role="status" key={key} size="sm">
        {label}
      </Pill>
    );

    if (!tooltip) {
      return pill;
    }

    return <MethodModuleStatus id={key} pill={pill} tooltipContent={tooltip} persistTooltip={persistTooltip} />;
  };

  return (
    <>
      {publishingState === 'preview' &&
        renderStatus({
          key: `preview-${id}`,
          label: t(`${BASE}.PREVIEW.LABEL`),
          tooltip: t(`${BASE}.PREVIEW.TOOLTIP.${canOpen ? 'ACCESS' : 'NO_ACCESS'}`, { role }),
          prominence: 'info',
        })}
      {publishingState === 'review' &&
        renderStatus({
          key: `review-${id}`,
          label: t(`${BASE}.REVIEW.LABEL`),
          tooltip: t(`${BASE}.REVIEW.TOOLTIP.VALUE`, { role }),
          prominence: 'info',
        })}
      {publishingState === 'phasedOut' &&
        renderStatus({
          key: `phasedOut-${id}`,
          label: t(`${BASE}.PHASED_OUT.LABEL`),
          tooltip: t(`${BASE}.PHASED_OUT.TOOLTIP.VALUE`, { role }),
          prominence: 'warning',
        })}
      {isUnlicensed &&
        renderStatus({
          key: `unlicensed-${id}`,
          label: t(`${BASE}.UNLICENSED.LABEL`),
          tooltip: t(`${BASE}.UNLICENSED.TOOLTIP.VALUE`),
          prominence: 'danger',
        })}
      {incomplete &&
        renderStatus({
          key: `incomplete-${id}`,
          label: t(`${BASE}.INCOMPLETE.LABEL`),
          tooltip: t(`${BASE}.INCOMPLETE.TOOLTIP.VALUE`),
          prominence: 'warning',
        })}
      {isDemo &&
        renderStatus({
          key: `demo-${id}`,
          label: t(`${BASE}.DEMO.LABEL`),
          tooltip: t(`${BASE}.DEMO.TOOLTIP.VALUE`),
          prominence: 'danger',
        })}
    </>
  );
}

export default ModuleStatus;
